import React, { useEffect, useMemo, useState } from "react";
import { Plus, Search, Users, Archive, ArchiveRestore, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/errors";
import { Card, EmptyState, PrimaryButton, QuietButton, Select } from "../../components/UI";
import { SubcontractorDialog } from "../../components/subby/SubcontractorDialog";

/** LT-37 — Libraries → Labour Rates → Directory. Subcontractors, filtered by Labour Rates trade. */
export function SubcontractorDirectory() {
  const [items, setItems] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tradeId, setTradeId] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [dialog, setDialog] = useState({ open: false, initial: null });

  const load = () => {
    setLoading(true);
    api.get("/subcontractors", { params: { q: q || undefined, trade_id: tradeId || undefined, include_archived: showArchived || undefined } })
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e, "Failed to load the directory")))
      .finally(() => setLoading(false));
  };
  useEffect(() => { api.get("/labour-rates").then((r) => setTrades(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [q, tradeId, showArchived]); // eslint-disable-line

  const tradeOptions = useMemo(() => [{ value: "", label: "All trades" }, ...trades.map((t) => ({ value: t.id, label: t.trade_name }))], [trades]);

  const toggleStatus = async (s) => {
    try {
      await api.post(`/subcontractors/${s.id}/${s.status === "archived" ? "activate" : "archive"}`);
      toast.success(s.status === "archived" ? `${s.company_name} restored` : `${s.company_name} archived`);
      load();
    } catch (e) { toast.error(formatApiError(e, "Could not change status")); }
  };

  return (
    <div data-testid="subby-directory">
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-4" />
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search company or contact…"
            data-testid="subby-directory-search"
            className="w-full h-9 pl-9 pr-3 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent" style={{ fontSize: 13 }} />
        </div>
        <div style={{ width: 220 }} data-testid="subby-directory-trade-filter">
          <Select value={tradeId} onChange={setTradeId} options={tradeOptions} />
        </div>
        <label className="inline-flex items-center gap-1.5 text-ink-3" style={{ fontSize: 12 }}>
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="subby-directory-show-archived" /> Show archived
        </label>
        <div className="flex-1" />
        <PrimaryButton icon={Plus} onClick={() => setDialog({ open: true, initial: null })} data-testid="subby-directory-add">Add subcontractor</PrimaryButton>
      </div>

      <Card>
        {loading ? (
          <div className="py-12 text-center text-ink-3" style={{ fontSize: 12 }}>Loading…</div>
        ) : items.length === 0 ? (
          <div data-testid="subby-directory-empty">
            <EmptyState icon={Users} title={q || tradeId ? "Nothing matches" : "No subcontractors yet"}
              hint="The directory feeds the Subby Quotes tab on every tender. A subby's trade comes from Labour Rates."
              action={!q && !tradeId ? <PrimaryButton icon={Plus} onClick={() => setDialog({ open: true, initial: null })}>Add subcontractor</PrimaryButton> : null} />
          </div>
        ) : (
          <table className="w-full text-left" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
            <thead><tr>
              <th className="col-head px-4 py-3" style={{ borderBottom: "1px solid #E2DDD2" }}>Company</th>
              <th className="col-head px-4 py-3" style={{ borderBottom: "1px solid #E2DDD2" }}>Contact</th>
              <th className="col-head px-4 py-3" style={{ borderBottom: "1px solid #E2DDD2" }}>Trades</th>
              <th className="col-head px-4 py-3 text-right" style={{ width: 90, borderBottom: "1px solid #E2DDD2" }}>Quotes</th>
              <th style={{ width: 170, borderBottom: "1px solid #E2DDD2" }} />
            </tr></thead>
            <tbody>
              {items.map((s, i) => (
                <tr key={s.id} data-testid={`subby-directory-row-${s.id}`}
                  className={["hover:bg-surface-2/60", i !== items.length - 1 ? "border-b border-line-soft" : "", s.status === "archived" ? "opacity-60" : ""].join(" ")}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }} data-testid={`subby-directory-name-${s.id}`}>{s.company_name}</span>
                      {s.status === "archived" && <span className="rounded font-mono" style={{ background: "#F0EEE6", color: "#7A6238", border: "1px solid #E2DDD2", padding: "1px 5px", fontSize: 9, fontWeight: 700, letterSpacing: "0.06em" }}>ARCHIVED</span>}
                    </div>
                    {s.notes && <div className="text-ink-3 mt-0.5 truncate" style={{ fontSize: 11, maxWidth: 320 }}>{s.notes}</div>}
                  </td>
                  <td className="px-4 py-3 text-ink-2" style={{ fontSize: 12.5 }}>
                    <div>{s.contact_name || "—"}</div>
                    <div className="text-ink-3" style={{ fontSize: 11 }}>{[s.contact_phone, s.contact_email].filter(Boolean).join(" · ")}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(s.trade_names || []).length === 0 ? <span className="text-ink-4" style={{ fontSize: 12 }}>—</span> :
                        s.trade_names.map((t) => <span key={t} className="rounded-full px-2 py-0.5 bg-surface-2 text-ink-2 border border-line" style={{ fontSize: 11 }}>{t}</span>)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono tnum text-ink-2" style={{ fontSize: 12.5 }} data-testid={`subby-directory-quotes-${s.id}`}>
                    {s.quotes_count || 0}
                    {s.accepted_count > 0 && <span className="ml-1.5 rounded font-mono" style={{ background: "#E8EFE9", color: "#2F4F3E", border: "1px solid #D6E2D8", padding: "1px 5px", fontSize: 9, fontWeight: 700 }} title="Accepted quotes" data-testid={`subby-directory-accepted-${s.id}`}>{s.accepted_count} WON</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <QuietButton icon={Pencil} onClick={() => setDialog({ open: true, initial: s })} data-testid={`subby-directory-edit-${s.id}`}>Edit</QuietButton>
                      <QuietButton icon={s.status === "archived" ? ArchiveRestore : Archive} onClick={() => toggleStatus(s)} data-testid={`subby-directory-archive-${s.id}`}>
                        {s.status === "archived" ? "Restore" : "Archive"}
                      </QuietButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <SubcontractorDialog open={dialog.open} initial={dialog.initial} trades={trades}
        onClose={() => setDialog({ open: false, initial: null })} onSaved={load} />
    </div>
  );
}
